import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import InstructorSidebar from "../common/instructorSidebar";
import AddQuestionModal from "./AddQuestionModal";
import EditQuestionModal from "./EditQuestionModal";
import { useDispatch } from "react-redux";
import {
  getQuizQuestions,
  createQuizQuestions,
  deleteQuizQuestions,
  uploadExcel,
} from "utils/quizThunk";
import toast from "react-hot-toast";

const InstructorQuizQuestions = () => {
  const { id } = useParams(); // Get quizId from URL as 'id'
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("manual");
  const [error, setError] = useState(null);
  const location = useLocation();
  const quiz = location?.state?.quiz;
  const [file, setFile] = useState(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10,
  });
  useEffect(() => {
    fetchQuestions();
  }, [pagination.currentPage,search]);

  // Fetch questions on component mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        quizId: id,
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        search
      };
      const result = await dispatch(getQuizQuestions(payload));
      if (getQuizQuestions?.fulfilled?.match(result)) {
        console.log("Fetched questions:", result.payload); 
        setPagination(result.payload?.pagination||{})// Debug
        setQuestions(result?.payload?.data || []);
      } else {
        console.log(result.payload?.message || "Failed to fetch questions");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async (values, { resetForm }) => {
    setLoading(true);
    try {
      const payload = {
        quizId: id,
        quizQuestion: values?.description,
        answers: values?.answers?.map((answer) => ({
          text: answer?.text,
          istrue: answer?.isCorrect,
        })),
        type:
          values?.questionType === "single"
            ? "single"
            : values?.questionType === "truefalse"
            ? "truefalse"
            : "multiple",
        // level: values?.questionLevel === "easy" ? "Easy" : values?.questionLevel === "medium" ? "Moderate" : "Hard",
        level: values?.questionLevel,
        language: values?.medium === "english" ? "english" : "hindi",
      };
      const result = await dispatch(createQuizQuestions(payload));
      if (createQuizQuestions.fulfilled.match(result)) {
        toast.success(
          result?.payload?.message || "Question created successfully"
        );
        resetForm();
        closeModal("add_question");
        fetchQuestions(); // Refresh questions
      } else {
        throw new Error(
          result?.payload?.message || "Failed to create question"
        );
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = async (values, questionId, { resetForm }) => {
    setLoading(true);
    try {
      const payload = {
        quizId: id,
        quizQuestion: values?.description,
        answers: values?.answers.map((answer) => ({
          text: answer?.text,
          istrue: answer?.isCorrect,
        })),
        type:
          values?.questionType === "single"
            ? "single"
            : values?.questionType === "truefalse"
            ? "truefalse"
            : "multiple",
        // level: values?.questionLevel === "easy" ? "Easy" : values?.questionLevel === "medium" ? "Moderate" : "Hard",
        level: values?.questionLevel,
        language: values?.medium === "english" ? "english" : "hindi",
        questionId,
      };
      const result = await dispatch(createQuizQuestions(payload));
      if (createQuizQuestions.fulfilled.match(result)) {
        toast.success(
          result?.payload?.message || "Question updated successfully"
        );
        resetForm();
        setEditingQuestion(null);
        closeModal("edit_question");
        fetchQuestions(); // Refresh questions
      } else {
        throw new Error(
          result?.payload?.message || "Failed to update question"
        );
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (question) => {
    setEditingQuestion({
      _id: question._id,
      description: question?.quizQuestion,
      questionType: question?.type,
      // questionLevel: question?.level === "Easy" ? "easy" : question.level === "Moderate" ? "medium" : "hard",
      questionLevel: question?.level,
      medium: question.language?.toLowerCase(),
      answers: question.answers?.map((answer) => ({
        text: answer?.text,
        isCorrect: answer?.istrue,
      })),
      correctAnswerIndex:
        question?.type !== "multiple"
          ? question?.answers?.findIndex((a) => a?.istrue)
          : -1,
    });
    const modal = document.getElementById("edit_question");
    if (modal) {
      if (window.bootstrap?.Modal) {
        try {
          const modalInstance =
            window.bootstrap.Modal.getInstance(modal) ||
            new window.bootstrap.Modal(modal);
          modalInstance.show();
        } catch (error) {
          console.error("Error showing modal:", error);
        }
      } else {
        modal.setAttribute("data-bs-toggle", "modal");
        modal.setAttribute("data-bs-target", "#edit_question");
        modal.click();
      }
    } else {
      console.error("Modal element not found");
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal && window.bootstrap?.Modal) {
      const modalInstance = window.bootstrap.Modal.getInstance(modal);
      modalInstance?.hide();
    }
  };

  const openAddModal = () => {
    const modal = document.getElementById("add_question");
    if (modal && window.bootstrap?.Modal) {
      const modalInstance =
        window.bootstrap.Modal.getInstance(modal) ||
        new window.bootstrap.Modal(modal);
      modalInstance.show();
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        questionId: questionToDelete?._id,
      };
      const result = await dispatch(deleteQuizQuestions(payload));

      if (deleteQuizQuestions.fulfilled.match(result)) {
        toast.success(
          result?.payload?.message || "Quiz Question Deleted Successfully"
        );

        // Close modal after successful deletion
        const modalEl = document.getElementById("delete_modal");
        if (modalEl && window.bootstrap?.Modal) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
          modalInstance?.hide();
        }

        setQuestionToDelete(null);
        fetchQuestions(); // Refresh questions
      } else {
        throw new Error(result.payload?.message || "Failed to delete question");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleExcelUpload = async () => {
    console.log("clicked");

    if (!file || !id) {
      toast.error("File or Quiz ID is missing");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("quizId", id);

    try {
      const res = await dispatch(uploadExcel(formData));
      if (res?.payload?.success) {
        toast.success(res?.payload?.message || "Excel uploaded successfully");
        closeModal("add_question");
        fetchQuestions();
        setFile(null);
      } else {
        toast.error(res?.payload?.message || "Excel not uploaded successfully");
        closeModal("add_question");
      }
    } catch (error) {
      toast.error("Something went wrong");
      closeModal("add_question");
    }
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  };
  const getPaginationItems = ({ currentPage, totalPages }) => {
  const pages = [];

  if (totalPages <= 6) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);
  }

  return pages;
};

  return (
    <>
      <Breadcrumb title="Quiz Questions" />
      <div className="content">
        <div className="container">
          {/* <ProfileCard /> */}
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9">
              <div className="card bg-light">
                <div className="card-body">
                  <div className="row align-items-center gy-3">
                    <div className="col-xl-6">
                      <h5 className="mb-2">
                        {questions[0]?.quizId?.quizTitle ||
                          quiz?.quizTitle ||
                          "Course Title"}
                      </h5>
                      <div className="question-info d-flex align-items-center">
                        <p className="d-flex align-items-center fs-14 me-2 pe-2 border-end mb-0">
                          <i className="isax isax-message-question5 text-primary-soft me-2"></i>
                          {pagination?.total} questions
                        </p>
                        <p className="d-flex align-items-center fs-14 mb-0">
                          <i className="isax isax-clock5 text-primary-soft me-2"></i>
                          {questions[0]?.quizId?.duration
                            ? `${questions[0]?.quizId?.duration} Hours`
                            : "0 Hours"}
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 d-flex align-items-center justify-content-sm-end">
                      <button
                        type="button"
                        className="btn btn-secondary rounded-pill"
                        onClick={() => {
                          openAddModal();
                          setType("manual");
                        }}
                        aria-label="Add new question"
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Add Question"}
                      </button>{" "}
                      &nbsp; &nbsp; &nbsp;
                      <button
                        type="button"
                        className="btn btn-secondary rounded-pill"
                        onClick={() => {
                          openAddModal();
                          setType("excel");
                        }}
                        aria-label="Add new question"
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Import Questions"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row my-3 ">
                <div className="col-md-12 d-flex justify-content-end">
                  <div className="position-relative col-12 col-md-4  ">
                    <input
                      type="text"
                      className="form-control ps-5"
                      placeholder="Search quizzes here..."
                      value={search}
                     onChange={(e) => {
                        setPagination((prev) => ({ ...prev, currentPage: 1 }));
                        setSearch(e.target.value);
                      }}
                    />
                    <span
                      className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                      style={{ pointerEvents: "none" }}
                    >
                      <i className="bi bi-search"></i>
                    </span>
                  </div>
                </div>
              </div>

              {loading && <div>Loading questions...</div>}
              {/* {error && <div className="text-danger">{error}</div>} */}
              {questions?.length === 0 && !loading && (
                <div>No questions available</div>
              )}
              {questions?.map((q) => (
                <div className="card mt-3" key={q?._id}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div
                        dangerouslySetInnerHTML={{ __html: q?.quizQuestion }}
                      />
                      <div>
                        <span
                          className={`badge text-white ${
                            q?.level === "easy"
                              ? "bg-success"
                              : q?.level === "medium"
                              ? "bg-warning"
                              : "bg-danger"
                          }`}
                        >
                          {q?.level}
                        </span>{" "}
                        <button
                          className="d-inline-flex fs-14 me-2 action-icon"
                          onClick={() => handleEditClick(q)}
                          aria-label={`Edit question ${q?.quizQuestion}`}
                        >
                          <i className="isax isax-edit-2"></i>
                        </button>
                        <button
                          className="d-inline-flex fs-14 action-icon"
                          onClick={() => {
                            setQuestionToDelete(q);
                            const modalEl =
                              document.getElementById("delete_modal");
                            if (modalEl) {
                              const modalInstance =
                                window.bootstrap.Modal.getInstance(modalEl) ||
                                new window.bootstrap.Modal(modalEl);
                              modalInstance.show();
                            }
                          }}
                          aria-label={`Delete question ${q?.quizQuestion}`}
                        >
                          <i className="isax isax-trash"></i>
                        </button>
                      </div>
                    </div>
                    {q?.answers?.map((a, idx) => (
                      <div className="form-check mb-1" key={idx}>
                        <input
                          className="form-check-input"
                          type={`${
                            q?.type === "MCQ" || q?.type == "multiple"
                              ? "checkbox"
                              : "radio"
                          }`}
                          checked={a?.istrue}
                          readOnly
                          aria-label={`Option ${idx + 1}: ${a?.text}`}
                        />
                        <label className="form-check-label">{a?.text}</label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <ul className="pagination mb-0">
  <li className={`page-item ${pagination.currentPage === 1 ? "disabled" : ""}`}>
    <button className="page-link" onClick={() => handlePageChange(pagination.currentPage - 1)}>
      <i className="fas fa-chevron-left"></i>
    </button>
  </li>

  {getPaginationItems(pagination).map((item, index) => (
    <li
      key={index}
      className={`page-item ${item === pagination.currentPage ? "active" : ""} ${
        item === "..." ? "disabled" : ""
      }`}
    >
      {item === "..." ? (
        <span className="page-link">...</span>
      ) : (
        <button className="page-link" onClick={() => handlePageChange(item)}>
          {item}
        </button>
      )}
    </li>
  ))}

  <li className={`page-item ${pagination.currentPage === pagination.totalPages ? "disabled" : ""}`}>
    <button className="page-link" onClick={() => handlePageChange(pagination.currentPage + 1)}>
      <i className="fas fa-chevron-right"></i>
    </button>
  </li>
</ul>

              </div>
            </div>
          </div>
        </div>
      </div>

      <AddQuestionModal
        handleSubmit={handleAddQuestion}
        handleExcelUpload={handleExcelUpload}
        setFile={setFile}
        closeModal={() => closeModal("add_question")}
        type={type}
      />
      <EditQuestionModal
        question={editingQuestion}
        handleSubmit={handleEditQuestion}
        closeModal={() => closeModal("edit_question")}
      />
      <div className="modal fade" id="delete_modal" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteModalLabel">
                Confirm Delete
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setQuestionToDelete(null)}
              ></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this question?
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary rounded-pill"
                data-bs-dismiss="modal"
                onClick={() => setQuestionToDelete(null)}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger rounded-pill"
                onClick={handleConfirmDelete}
                aria-label="Delete question"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstructorQuizQuestions;
